import type { TranslationCatalog } from "../types.js";

// Romanian requires "de" before the plural noun for numbers >= 20
// ("20 de monede") but not for 2-19 ("5 monede") — handled here as a
// self-contained "count + noun" phrase rather than a bare noun form, since
// the "de" placement depends on the number itself, not just its plural class.
function coinPhrase(n: number): string {
  if (n === 1) return `${n} monedă`;
  if (n >= 20) return `${n} de monede`;
  return `${n} monede`;
}

export default {
  "common.loading": "Se conectează...",
  "common.loadingEllipsis": "Se încarcă...",
  "common.unknownError": "Eroare necunoscută",
  "common.errorPrefix": (p: { message: string }) => `Eroare: ${p.message}`,
  "common.retry": "Încearcă din nou",
  "common.close": "Închide",
  "common.back": "← Înapoi",
  "common.cancel": "Anulează",
  "common.details": "Detalii",
  "common.settingsTitle": "⚙️ Setări",
  "common.profile": "👤 Profil",
  "common.rules": "📖 Reguli",

  "login.subtitle": "Autentifică-te pentru a juca.",
  "login.continueFacebook": "Continuă cu contul Facebook",
  "login.continueGoogle": "Continuă cu contul Google",
  "login.continueEmail": "Continuă cu adresa de e-mail",
  "login.tagline": "Joc de cărți cu atu și licitație pentru trei jucători. Licitează, alege atuul și ia levele — primul care ajunge la 101 puncte câștigă.",
  "login.linkRules": "Reguli",
  "login.linkStrategy": "Strategie",
  "login.linkAbout": "Despre joc",
  "login.linkPrivacy": "Confidențialitate",

  "lobby.entryFee": (p: { fee: number }) => `Taxă de intrare: ${coinPhrase(p.fee)}`,
  "lobby.resumeMatch": "Continuă partida",
  "lobby.playButton": "Joacă (3 jucători)",
  "lobby.inviteFriend": "📣 Invită un prieten",
  "lobby.insufficientFunds": (p: { missing: number }) => `Nu ai suficiente monede pentru taxa de intrare (îți lipsesc ${coinPhrase(p.missing)}).`,
  "lobby.watchAd": "Urmărește o reclamă pentru monede în plus",

  "leaderboard.title": "🏆 Clasament",
  "leaderboard.emptySeason": "Încă nu există jucători clasați în acest sezon (trebuie jucată cel puțin 1 partidă).",
  "leaderboard.emptyGlobal": "Încă nu există jucători clasați (trebuie jucată cel puțin 1 partidă în total).",
  "leaderboard.seasonTabDefault": "Sezon",
  "leaderboard.seasonTabWithDate": (p: { month: string; year: string }) => `Sezon ${p.month}/${p.year}`,
  "leaderboard.globalTab": "🌍 Total (toate sezoanele)",
  "leaderboard.colPlayer": "Jucător",
  "leaderboard.colPoints": "Puncte",
  "leaderboard.colMatches": "Partide",
  "leaderboard.you": "Tu",
  "leaderboard.yourScoreUnranked": (p: { points: number; matchesPlayed: number }) =>
    `Scorul tău: ${p.points} puncte din ${p.matchesPlayed} partide (ai nevoie de cel puțin 1 partidă pentru clasament)`,

  "rules.title": "📖 Cum se joacă Meksiko",
  "rules.cardsHeading": "Cărți",
  "rules.cardsBody":
    "Se joacă cu 32 de cărți (7, 8, 9, 10, J, Q, K, A în toate cele 4 culori). Puterea cărților în cadrul unei culori, de la cea mai slabă: 7 &lt; 8 &lt; 9 &lt; 10 &lt; J &lt; Q &lt; K &lt; A. Culoarea de atu bate toate cărțile care nu sunt de atu, indiferent de valoare.",
  "rules.dealingHeading": "Împărțirea cărților",
  "rules.dealingBody": 'Fiecare dintre cei 3 jucători primește 10 cărți (în două runde de câte 5), iar 2 cărți rămân ascunse în "talon".',
  "rules.biddingHeading": "Licitația",
  "rules.biddingBody":
    'Primul jucător trebuie să liciteze cel puțin 5. Fiecare jucător următor trebuie să liciteze strict mai mult (5, 6, 7, 8, 9, 10, sau "MEKSIKO") sau să spună "pas" — cine spune odată "pas", iese definitiv din licitație pentru acea mână. Licitația se încheie când rămâne un singur jucător.',
  "rules.talonHeading": "Talonul și decartarea",
  "rules.talonBody":
    "Câștigătorul licitației deschide talonul (2 cărți, vizibile tuturor), le adaugă în mână (are acum 12 cărți), apoi decartează 2 cărți cu fața în jos. Apoi alege culoarea de atu.",
  "rules.mexicoHeading": "MEKSIKO (20)",
  "rules.mexicoBody":
    'Cine licitează "MEKSIKO" se angajează să câștige toate cele 10 levate. Se joacă fără atu și fără talon — doar cu cele 10 cărți inițiale.',
  "rules.tricksHeading": "Desfășurarea jocului (levatele)",
  "rules.tricksBody":
    "Trebuie să urmezi culoarea jucată, dacă o ai. Dacă nu o ai, trebuie să joci o carte de atu, dacă ai (tăiere) — doar dacă nu ai niciuna dintre ele, poți juca orice carte. Levata este câștigată de cea mai puternică carte din culoarea jucată, cu excepția cazului în care cineva a jucat atu — atunci câștigă cel mai puternic atu, indiferent de culoare. Cine câștigă levata, începe următoarea.",
  "rules.scoringHeading": "Punctaj",
  "rules.scoringBody":
    "Câștigătorul licitației: dacă ia cel puțin atâtea levate câte a licitat, primește atâtea puncte câte a luat de fapt. Dacă nu reușește, primește puncte <strong>negative</strong> egale cu valoarea licitației. Ceilalți doi jucători primesc întotdeauna puncte egale cu numărul de levate pe care le-au luat.",
  "rules.matchEndHeading": "Sfârșitul partidei",
  "rules.matchEndBody":
    "Partida se termină când cineva atinge scorul țintă. Dacă mai mulți jucători depășesc ținta în aceeași mână cu același scor maxim, se joacă o mână suplimentară până se rezolvă.",

  "settings.soundOff": "🔇 Sunet oprit",
  "settings.soundOn": "🔊 Sunet pornit",
  "settings.displayNameLabel": "Nume în joc",
  "settings.saveDisplayName": "Salvează numele",
  "settings.displayNameLengthError": "Numele trebuie să aibă între 1 și 20 de caractere.",
  "settings.saveFailed": "Salvarea a eșuat, încearcă din nou.",
  "settings.logout": "🚪 Deconectare",

  "waitingRoom.waitingForPlayers": (p: { filled: number; total: number }) => `Se așteaptă jucători... ${p.filled}/${p.total}`,
  "waitingRoom.countdown": (p: { remainingSec: number }) => `Mai sunt ${p.remainingSec}s până când AI completează locurile goale`,

  "game.passLabel": "pas",
  "game.passButton": "Pas",
  "game.mexicoLabel": "MEKSIKO",
  "game.bidLabel": (p: { bid: string }) => `Licitație: ${p.bid}`,
  "game.trumpLabel": (p: { suit: string }) => `Atu: ${p.suit}`,
  "game.tricksWonLabel": (p: { count: number }) => `Levate luate: ${p.count}`,
  "game.biddingInProgress": "Licitația este în desfășurare...",
  "game.yourBid": "Licitația ta",
  "game.someoneDiscarding": (p: { name: string }) => `${p.name} decartează...`,
  "game.someoneChoosingTrump": (p: { name: string }) => `${p.name} alege atuul...`,
  "game.chooseTrumpPrompt": "Alege atuul",
  "game.nextDealPause": "Următoarea împărțire...",
  "game.fromTalon": "din talon",
  "game.talonRevealLabel": (p: { bidder: string }) => `Cărțile din talon (${p.bidder})`,
  "game.yourTurnLabel": "Rândul tău",
  "game.turnTimerLabel": (p: { name: string; remainingSec: number }) => `${p.name} — ${p.remainingSec}s`,
  "game.idleBotTakeover": "🤖 Nu ai jucat la timp, așa că AI joacă acum în locul tău. Apasă oriunde pentru a prelua controlul.",
  "game.leaveMatch": "Părăsește partida",
  "game.cardAlt": "carte",
  "game.botDisplayName": "Jucător AI",
  "game.botBadgeIdle": "🤖 AI (temporar)",
  "game.botBadgeFull": "🤖 BOT",
  "game.declarerTooltip": "Conduce licitația",

  "matchEnd.title": "Partidă încheiată",
  "matchEnd.winner": (p: { name: string }) => `Câștigător: ${p.name}`,
  "matchEnd.backToLobby": "Înapoi în lobby",

  "reactions.sendButton": "Trimite o reacție",
  "reaction.angry_small_card": "De ce cu cartea mică?",
  "reaction.angry_big_card": "De ce cu cartea mare?",
  "reaction.bravo": "Bravo",
  "reaction.svaka_cast": "Felicitări",
  "reaction.cestitam_pobeda": "Felicitări pentru victorie",
  "reaction.izvini": "Scuze",
  "reaction.pa_zasto": "Dar de ce!",
  "reaction.srecan": "",
  "reaction.srce": "",
  "reaction.pozdrav": "",

  "medal.season_gold": "🥇 Sezon",
  "medal.season_silver": "🥈 Sezon",
  "medal.season_bronze": "🥉 Sezon",
  "medal.first_match": "🎉 Prima partidă",
  "medal.matches_100": "💯 100 de partide",
  "medal.mexico_master": "🌵 Maestru Meksiko",
  "medal.hat_trick_3_wins": "🔥 Hat-trick (3 victorii)",
  "medal.tricks_1000": "🃏 1000 de levate",

  "suit.diamonds": "Caro",
  "suit.hearts": "Inimă",
  "suit.clubs": "Treflă",
  "suit.leaves": "Frunză",

  "profile.matchesPlayed": "Partide jucate",
  "profile.firstPlace": (p: { wins: number }) => `🥇 Locul 1 (${p.wins})`,
  "profile.secondPlace": (p: { count: number }) => `🥈 Locul 2 (${p.count})`,
  "profile.thirdPlace": (p: { count: number }) => `🥉 Locul 3 (${p.count})`,
  "profile.pointsConfigNote": (p: { win: number; second: number; third: number }) =>
    `Punctaj pe partidă: locul 1: ${p.win}, locul 2: ${p.second}, locul 3: ${p.third} pct`,
  "profile.noMedals": "Încă nu ai câștigat medalii.",
  "profile.medalsHeading": "Medalii",
  "profile.seasonHistoryHeading": "Istoricul sezoanelor",
  "profile.colSeason": "Sezon",
  "profile.colRank": "Loc",
  "profile.deleteAccountButton": "🗑️ Șterge contul",
  "profile.deleteAccountTitle": "Șterge contul",
  "profile.deleteAccountWarning":
    "Aceasta îți șterge definitiv contul și toate datele (istoricul partidelor, clasamentul, medaliile, soldul de monede). Această acțiune nu poate fi anulată.",
  "profile.deleteAccountConfirm": "Da, șterge contul",
  "profile.deleteAccountFailed": "Ștergerea a eșuat, încearcă din nou.",
  "profile.deleteAccountActiveMatch": "Nu poți șterge contul cât timp ești într-o partidă activă. Termină sau părăsește partida și încearcă din nou.",

  "errors.NOT_YOUR_TURN": "Nu este rândul tău.",
  "errors.INVALID_BID": "Licitație nevalidă.",
  "errors.BID_TOO_LOW": "Licitația trebuie să fie mai mare decât oferta curentă cea mai mare.",
  "errors.MUST_BID_FIRST": "Primul jucător trebuie să liciteze cel puțin 5, nu poate spune pas.",
  "errors.DUPLICATE_DISCARD": "Nu poți decarta aceeași carte de două ori.",
  "errors.CARD_NOT_IN_HAND": "Cartea aceea nu este în mâna ta.",
  "errors.MUST_FOLLOW_SUIT": "Trebuie să urmezi culoarea sau să tai cu atu dacă nu poți.",
  "errors.TRICK_INCOMPLETE": "Levata nu s-a încheiat încă.",
  "errors.MATCH_ENDED": "Partida s-a încheiat deja.",
  "errors.UNKNOWN_ACTION": "Acțiune necunoscută.",
  "errors.WRONG_PHASE": "Această acțiune nu este permisă în această fază a jocului.",
  "errors.NOT_BID_WINNER": "Doar câștigătorul licitației poate face asta.",
  "errors.MATCH_NOT_ENDED": "Partida nu s-a încheiat încă.",
  "errors.invalid_session_token": "Sesiunea a expirat, autentifică-te din nou.",
  "errors.room_full": "Masa este plină, încearcă din nou peste puțin timp.",
  "errors.insufficient_balance": "Nu ai suficiente monede pentru taxa de intrare.",
  "errors.ad_unavailable": "Reclama nu este disponibilă momentan. Verifică conexiunea la internet (sau dezactivează AdBlock) și încearcă din nou.",
  "errors.invalid_or_early_ticket": "Reclama nu a fost recunoscută ca vizionată. Încearcă din nou.",
  "errors.daily_ad_limit_reached": "Ai atins limita zilnică de reclame. Încearcă mâine.",

  "share.inviteText": "Joacă Meksiko cu mine!",
  "ad.placeholderText": "Reclamă simulată (demo web standalone)",
  "rotateOverlay.text": "Întoarce telefonul pentru a juca Meksiko",
  "languageSwitcher.ariaLabel": "Limbă",
} satisfies TranslationCatalog;
